import React, { ChangeEvent, Fragment, ReactElement, useState } from "react";
import { IColumn, IParentSize, IProduction, Order } from "../../../types";
import {
  formatDateTimeWithSeconds,
  getComparator,
  stableSort
} from "../../../system/utils";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination
} from "@mui/material";
import ProductionTableRow from "./productionTableRow";
import TableContainer  from "../../../components/tableContainer";
import TablePaginationActions from "../../../components/tablePaginationActions";

interface IProps {
  productionData: IProduction[];
  parentSize: IParentSize;
}

const columns: IColumn[] = [
  {
    id: "collapse",
    label: "",
    minWidth: 30,
  },
  {
    id: "reference",
    label: "Heat Number",
    minWidth: 140,
  },
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 200,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 200,
    format: value => formatDateTimeWithSeconds(value),
  },
];

const ProductionTable = ({ productionData, parentSize }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IProduction>("beginDatetime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const dataLength = productionData ? productionData.length : 0;
  const tableContainerHeight = parentSize.height - 50;

  const createSortHandler = (property: keyof IProduction) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="flex-grow-1">
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true}  size={"small"}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) =>
                <TableCell
                  key={`${column.id}-${index}`}
                  align={column.align}
                  style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth, width: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}>
                  {column.id !== "actions"
                    ? <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof IProduction)}
                    >
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                    : <Fragment />}
                </TableCell>
              )}
              <TableCell style={{ backgroundColor: "#b0bec5" }}/>
              <TableCell style={{ backgroundColor: "#b0bec5" }}/>
              <TableCell style={{ backgroundColor: "#b0bec5" }}/>
              <TableCell style={{ backgroundColor: "#b0bec5" }}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {productionData && stableSort<IProduction>(productionData, getComparator(order, orderBy))
              .map((row: IProduction, index: number) => <ProductionTableRow production={row} key={`prod-eaf-row-${index}`} /> )}
          </TableBody>
        </Table>
        {!productionData &&
        <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
          <CircularProgress className=""/>
        </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductionTable;
